/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Component, Watch} from 'vue-property-decorator';
import IssuesList from '@/components/IssuesList/index.vue';
import {Action, Getter, State} from 'vuex-class';
import {FormStateInterface, ReportStageInterface} from '@/store/modules/form/types';
import {
    GameCategoryInterface,
    GameInterface,
    GamePlatformInterface,
    GameVersionInterface,
} from '@/store/modules/games/types';
import {UserDetailsInterface} from '@/store/modules/user-session/types';
import Loading from '@/components/Loading/index.vue';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import {mixins} from 'vue-class-component';
import Scroller from '@/mixins/Scroller';


/**
 * Report Stage 1.Report component.
 *
 * @author nchapman@frontier.co.uk
 *
 */
@Component({
    name: 'c-report-stage-report',
    components: {
        'c-loading': Loading,
        'c-issues-list': IssuesList,
    },
})
export default class ReportStageReport extends mixins(Scroller) {
    // Forms
    @State('formStateModule') public formState!: FormStateInterface;
    @Action('cancelReportIssue', {namespace: 'formStateModule'}) public cancelReportIssue!: () => void;
    @Action('updateReportStage', {namespace: 'formStateModule'})
    public updateReportStage!: (report: ReportStageInterface) => void;
    @Action('updateStage', {namespace: 'formStateModule'}) public updateStage!: (stage: number) => void;

    @Action('updateGameSelected', {namespace: 'formStateModule'})
    public updateGameSelected!: (game: number) => void;

    // Games
    @Action('fetchGames', {namespace: 'gamesStateModule'}) public fetchGames!: () => Promise<void>;
    @Getter('getGames', {namespace: 'gamesStateModule'}) public getGames!: GameInterface[];

    // User Details
    @Getter('getUserDetails', {namespace: 'userSessionModule'}) public getUserDetails!: UserDetailsInterface;

    // Settings masthead
    @Action('updateMastheadImage', {namespace: 'settingsModule'})
    public updateMastheadImage!: (imageSlug: string | undefined) => void;

    public selectedGame: number | null = null;
    public selectedPlatform: number | null = null;
    public selectedVersion: number | null = null;
    public selectedCategory: number | null = null;
    public problem: string = '';
    public state: LoadingStateInterface = new LoadingState(false, false);
    public debounceInput: Debounce = this.$debounce(this.updateFormValues, 500);
    private disabledSubmittedGames: string[] = [];

    /**
     * Mounted life-cycle event.
     *
     * Initialises the loader.
     */
    public async mounted(): Promise<void> {
        history.pushState({page: 1}, 'Report', '?step=1');

        try {
            this.state.start();
            await this.fetchGames();
            for (const $eachGame of this.getGames) {
                if ($eachGame.readonly === true) {
                    this.disabledSubmittedGames.push($eachGame.name);
                }
            }
            for (const eachGame of this.getGames) {
                if (eachGame.readonly !== true) {
                    this.selectedGame = await this.formState.reportStage.game || eachGame.id;
                    break;
                }
            }
            this.selectedPlatform = this.formState.reportStage.platform || this.getGames[0].platforms[0].id;
            this.selectedVersion = this.formState.reportStage.version || this.getGames[0].versions[0].id;
            this.selectedCategory = this.formState.reportStage.category || this.getGames[0].categories[0].id;
            this.problem = this.formState.reportStage.problemTitle || '';
            this.state.complete();
            if (this.getGames[0].branding !== null && this.getGames[0].branding.key_art !== null) {
                this.updateMastheadImage(this.getGames[0].branding.key_art.url);
            }
        } catch (e) {
            this.state.failed(e.message);
        } finally {
            this.scrollTop();
        }

    }

    /**
     * Destroyed lifecycle hook
     */
    public destroyed(): void {
        this.updateMastheadImage(undefined);
    }

    /**
     * Get the current games platforms
     *
     * @return {GamePlatformInterface[]}
     */
    public get platforms(): GamePlatformInterface[] {
        return (this.currentGame) ? this.currentGame.platforms.sort(
            (a: GamePlatformInterface, b: GamePlatformInterface) => (a.name > b.name) ? 1 : -1,
        ) : [];
    }

    /**
     * Get the current games versions
     *
     * @return {GameVersionInterface[]}
     */
    public get versions(): GameVersionInterface[] {

        const versions: GameVersionInterface[] = [];

        if (this.currentGame && undefined !== this.currentGame.versions) {
            for (const version of this.currentGame.versions) {
                if (version.enabled === true) {
                    versions.push(version);
                }
            }
        }
        return versions;
    }

    /**
     * Get the current games available categories
     *
     * @return {GameCategoryInterface[]}
     */
    public get categories(): GameCategoryInterface[] {
        return (this.currentGame) ? this.currentGame.categories.sort(
            (a: GameCategoryInterface, b: GameCategoryInterface) => (a.name > b.name) ? 1 : -1,
        ) : [];
    }

    /**
     * Get the current selected game object
     *
     * @return {GameInterface | undefined}
     */
    public get currentGame(): GameInterface | undefined {
        return this.getGames.find((game: GameInterface) => {
            return game.id === this.selectedGame;
        });
    }

    /**
     * Get the available submitted games.
     *
     * @return {GameInterface[] | undefined}
     */
    public get availableGame(): GameInterface[] | undefined {
        return this.getGames.filter((game: GameInterface) => {
            return !this.disabledSubmittedGames.includes(game.name);
        });
    }

    /**
     * Is the form valid ?
     *
     * @return {boolean}
     */
    public get isValid(): boolean {
        return this.problem.length > 30 && this.problem.length < 250;
    }

    /**
     * Update the state and ensure select fields have valid options set
     *
     * @return {Promise<void>}
     */
    public async updateFormValues(): Promise<void> {
        if (this.currentGame) {
            // undefined key art reverts masthead to default Frontier image.
            const keyArt: string|undefined = this.getKeyArt(this.currentGame);
            this.updateMastheadImage(keyArt);

            if (undefined === this.currentGame.categories.find((category: GameCategoryInterface) => {
                return category.id === this.selectedCategory;
            })) {
                this.selectedCategory = this.currentGame.categories[0].id || null;
            }

            if (undefined === this.currentGame.platforms.find((platform: GamePlatformInterface) => {
                return platform.id === this.selectedPlatform;
            })) {
                this.selectedPlatform = this.currentGame.platforms[0].id || null;
            }

            if (undefined === this.currentGame.versions.find((version: GameVersionInterface) => {
                return version.id === this.selectedVersion;
            })) {
                this.selectedVersion = this.currentGame.versions[0].id || null;
            }

            this.updateReportStage({
                problemTitle: this.problem,
                game: this.selectedGame || undefined,
                platform: this.selectedPlatform || undefined,
                version: this.selectedVersion || undefined,
                category: this.selectedCategory || undefined,
            });
        }
    }


    /**
     *  Progress to the next step if form values are valid.
     *
     */
    public next(): void {
        this.$validator.validateAll().then((result: boolean) => {
            if (result) {
                this.updateStage(this.formState.stage + 1);
            }
        });
    }

    @Watch('selectedGame')
    public updateSelectedGame(): void {
        if (this.selectedGame) {
            this.updateGameSelected(this.selectedGame);
        }
    }

    private getKeyArt(game: GameInterface): string|undefined {
        if (game.branding !== null && game.branding.key_art !== null) {
            return game.branding.key_art.url;
        }
        return undefined;
    }
}
